import Decimal from 'decimal.js';

import type { Currency } from '../../server/contracts';
import { tradeInputSchema, type TradeInput } from './schema';

export type MoneySummary = Readonly<{
  lines: readonly (string | null)[];
  subtotal: string | null;
  total: string | null;
}>;

export type CalculateAmountsOptions = Readonly<{
  missingAdjustmentsAsZero?: boolean;
  missingItemPricesAsZero?: boolean;
}>;

export function calculateAmounts(input: TradeInput, options: CalculateAmountsOptions = {}): MoneySummary {
  const normalized = tradeInputSchema.parse(input);
  const values = normalized.items.map((item) => {
    if (item.quantity === null) return null;
    const unitPrice = item.unitPrice ?? (options.missingItemPricesAsZero ? '0' : null);
    return unitPrice === null
      ? null
      : new Decimal(item.quantity).mul(unitPrice).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  });
  const lines = values.map((value) => value === null ? null : value.toFixed(2));
  if (values.length === 0 || values.some((value) => value === null)) {
    return { lines, subtotal: null, total: null };
  }

  const rounded = values.filter((value): value is Decimal => value !== null);
  const subtotal = rounded.reduce((sum, line) => sum.plus(line), new Decimal(0));
  if (!options.missingAdjustmentsAsZero && (
    normalized.freight === null ||
    normalized.insurance === null ||
    normalized.otherCharges === null ||
    normalized.discount === null
  )) {
    return { lines, subtotal: subtotal.toFixed(2), total: null };
  }
  const freight = normalized.freight ?? '0';
  const insurance = normalized.insurance ?? '0';
  const otherCharges = normalized.otherCharges ?? '0';
  const discount = normalized.discount ?? '0';
  const total = subtotal
    .plus(freight)
    .plus(insurance)
    .plus(otherCharges)
    .minus(discount);
  return { lines, subtotal: subtotal.toFixed(2), total: total.toFixed(2) };
}

const currencyWords: Readonly<Record<Currency, Readonly<{ major: string; minor: string }>>> = {
  USD: { major: 'US DOLLARS', minor: 'CENTS' },
  EUR: { major: 'EUROS', minor: 'CENTS' },
  GBP: { major: 'POUNDS', minor: 'PENCE' },
  HKD: { major: 'HONG KONG DOLLARS', minor: 'CENTS' },
  CNY: { major: 'CHINESE YUAN', minor: 'FEN' },
};

export function amountInWords(amount: string, currency: Currency): string {
  if (!/^(?:0|[1-9]\d{0,14})(?:\.\d{1,2})?$/.test(amount)) {
    throw new Error('Amount must be a nonnegative decimal string with at most 2 decimal places.');
  }
  const [majorText, minorText = ''] = amount.split('.');
  const major = BigInt(majorText);
  const minor = BigInt((minorText + '00').slice(0, 2));
  const names = currencyWords[currency];
  return `${integerWords(major)} ${names.major} AND ${integerWords(minor)} ${names.minor} ONLY`;
}

const ones = [
  'ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
  'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN',
  'SEVENTEEN', 'EIGHTEEN', 'NINETEEN',
] as const;
const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'] as const;
const scales = [
  [1_000_000_000_000n, 'TRILLION'],
  [1_000_000_000n, 'BILLION'],
  [1_000_000n, 'MILLION'],
  [1_000n, 'THOUSAND'],
] as const;

function integerWords(value: bigint): string {
  if (value < 20n) return ones[Number(value)];
  for (const [scale, label] of scales) {
    if (value >= scale) {
      const quotient = value / scale;
      const remainder = value % scale;
      return `${integerWords(quotient)} ${label}${remainder === 0n ? '' : ` ${integerWords(remainder)}`}`;
    }
  }
  if (value >= 100n) {
    const remainder = value % 100n;
    return `${ones[Number(value / 100n)]} HUNDRED${remainder === 0n ? '' : ` ${integerWords(remainder)}`}`;
  }
  const remainder = value % 10n;
  return `${tens[Number(value / 10n)]}${remainder === 0n ? '' : ` ${ones[Number(remainder)]}`}`;
}
